import React, { Component } from 'react';

interface TabKeyBlockerProps {
  children: React.ReactNode;
  allowInModals?: boolean;
  showWarning?: boolean;
  warningMessage?: string;
  // New prop to scope the blocking to container only
  scopeToContainer?: boolean;
}

interface TabKeyBlockerState {
  isActive: boolean;
}

class TabKeyBlocker extends Component<TabKeyBlockerProps, TabKeyBlockerState> {
  private originalTabIndexes: Map<Element, number> = new Map();
  private iframeLoadListeners: Map<HTMLIFrameElement, () => void> = new Map();
  private containerRef: React.RefObject<HTMLDivElement>;

  constructor(props: TabKeyBlockerProps) {
    super(props);
    this.state = {
      isActive: true
    };
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this.enableTabBlocking();
  }

  componentDidUpdate(prevProps: TabKeyBlockerProps) {
    if (prevProps.allowInModals !== this.props.allowInModals) {
      this.disableTabBlocking();
      this.enableTabBlocking();
    }
  }

  componentWillUnmount() {
    this.disableTabBlocking();
  }

  // Enable/Disable Tab blocking
  enableTabBlocking = () => {
    if (!this.state.isActive) return;
    
    if (this.props.scopeToContainer && this.containerRef.current) {
      // Only listen to events within the container
      this.containerRef.current.addEventListener('keydown', this.handleKeyDown, true);
      this.disableTabIndexOnNonInputs(this.containerRef.current);
      this.setupIframeBlocking(this.containerRef.current);
    } else {
      // Global blocking (original behavior)
      document.addEventListener('keydown', this.handleKeyDown, true);
      window.addEventListener('keydown', this.handleKeyDown, true);
      this.disableTabIndexOnNonInputs();
      this.setupIframeBlocking();
    }
  };

  disableTabBlocking = () => {
    if (this.props.scopeToContainer && this.containerRef.current) {
      this.containerRef.current.removeEventListener('keydown', this.handleKeyDown, true);
      this.restoreTabIndexes(this.containerRef.current);
      this.cleanupIframeBlocking(this.containerRef.current);
    } else {
      document.removeEventListener('keydown', this.handleKeyDown, true);
      window.removeEventListener('keydown', this.handleKeyDown, true);
      this.restoreTabIndexes();
      this.cleanupIframeBlocking();
    }
  };

  toggleTabBlocking = (active: boolean) => {
    this.setState({ isActive: active }, () => {
      if (active) {
        this.enableTabBlocking();
      } else {
        this.disableTabBlocking();
      }
    });
  };

  // Key handling - scoped version
  handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab' || e.keyCode === 9) {
      // Check if we should allow Tab in modals
      const target = e.target as HTMLElement;
      const isInModal = this.props.allowInModals && target.closest('.modal');
      
      if (!isInModal) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        if (this.props.showWarning) {
          this.showWarning();
        }
        
        // Move focus to first input if needed
        this.focusFirstInput(e.shiftKey);
        
        return false;
      }
    }
  };

  // Focus management - scoped version
  focusFirstInput = (isShiftKey: boolean) => {
    const focusableElements = this.getFocusableElements();
    
    if (focusableElements.length === 0) return;
    
    const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as Element);
    let nextIndex;
    
    if (isShiftKey) {
      // Shift+Tab: move to previous
      nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
    } else {
      // Tab: move to next
      nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
    }
    
    (focusableElements[nextIndex] as HTMLElement).focus();
  };

  getFocusableElements = (container?: Element): Element[] => {
    const root = container || document;
    const queryRoot = container ? container : document;
    
    return Array.from(queryRoot.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"]), ' +
      'textarea:not([disabled]):not([tabindex="-1"]), ' +
      'select:not([disabled]):not([tabindex="-1"]), ' +
      'button:not([disabled]):not([tabindex="-1"]), ' +
      'a[href]:not([tabindex="-1"]), ' +
      '[contenteditable="true"]:not([tabindex="-1"]), ' +
      '[tabindex]:not([tabindex="-1"])'
    ));
  };

  // TabIndex management - scoped version
  disableTabIndexOnNonInputs = (container?: Element) => {
    const root = container || document;
    const allElements = root.querySelectorAll('*');
    
    allElements.forEach((element) => {
      const el = element as HTMLElement;
      
      // Skip input elements
      if (this.isInputElement(el)) {
        return;
      }
      
      // Store original tabindex
      if (el.hasAttribute('tabindex')) {
        const originalValue = el.getAttribute('tabindex');
        if (originalValue) {
          this.originalTabIndexes.set(el, parseInt(originalValue));
        }
      }
      
      // Set tabindex to -1 for non-input elements
      el.tabIndex = -1;
    });
  };

  restoreTabIndexes = (container?: Element) => {
    const root = container || document;
    const elementsToRestore: Element[] = [];
    
    // Collect elements that are within the container
    this.originalTabIndexes.forEach((_, element) => {
      if (!container || root.contains(element)) {
        elementsToRestore.push(element);
      }
    });
    
    // Restore collected elements
    elementsToRestore.forEach((element) => {
      const originalValue = this.originalTabIndexes.get(element);
      if (originalValue !== undefined) {
        (element as HTMLElement).tabIndex = originalValue;
        this.originalTabIndexes.delete(element);
      }
    });
  };

  // Input element detection
  isInputElement = (element: HTMLElement): boolean => {
    const tagName = element.tagName;
    const role = element.getAttribute('role');
    const contentEditable = element.getAttribute('contenteditable');
    
    return (
      tagName === 'INPUT' ||
      tagName === 'TEXTAREA' ||
      tagName === 'SELECT' ||
      tagName === 'BUTTON' ||
      contentEditable === 'true' ||
      role === 'textbox' ||
      role === 'button' ||
      element.isContentEditable
    );
  };

  // Iframe handling - scoped version
  setupIframeBlocking = (container?: Element) => {
    const root = container || document;
    const iframes = root.querySelectorAll('iframe');
    
    iframes.forEach((iframe) => {
      const handleIframeLoad = () => {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc) {
            iframeDoc.addEventListener('keydown', this.handleKeyDown, true);
          }
        } catch (e) {
          // Cross-origin iframe, can't access
        }
      };
      
      this.iframeLoadListeners.set(iframe, handleIframeLoad);
      iframe.addEventListener('load', handleIframeLoad);
      handleIframeLoad(); // Try immediately
    });
  };

  cleanupIframeBlocking = (container?: Element) => {
    const iframesToCleanup: HTMLIFrameElement[] = [];
    
    this.iframeLoadListeners.forEach((listener, iframe) => {
      if (!container || (container && container.contains(iframe))) {
        iframesToCleanup.push(iframe);
      }
    });
    
    iframesToCleanup.forEach((iframe) => {
      iframe.removeEventListener('load', this.iframeLoadListeners.get(iframe)!);
      
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          iframeDoc.removeEventListener('keydown', this.handleKeyDown, true);
        }
      } catch (e) {
        // Cross-origin iframe, can't access
      }
      
      this.iframeLoadListeners.delete(iframe);
    });
  };

  // Warning display
  showWarning = () => {
    const message = this.props.warningMessage || "Tab key navigation is disabled";
    
    // Create warning element
    const warningDiv = document.createElement('div');
    warningDiv.className = 'tab-key-warning';
    warningDiv.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ffcc00;
        color: #000;
        padding: 10px 15px;
        border-radius: 5px;
        z-index: 9999;
        font-size: 14px;
        box-shadow: 0 2px 5px rgba(0,0,0,0.2);
        animation: fadeInOut 3s ease-in-out;
      ">
        ${message}
      </div>
    `;
    
    // Add styles for animation
    if (!document.querySelector('#tab-key-warning-styles')) {
      const style = document.createElement('style');
      style.id = 'tab-key-warning-styles';
      style.innerHTML = `
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(-20px); }
          15% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-20px); }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(warningDiv);
    
    // Remove after animation
    setTimeout(() => {
      if (warningDiv.parentNode) {
        warningDiv.parentNode.removeChild(warningDiv);
      }
    }, 3000);
  };

  render() {
    return (
      <div 
        ref={this.containerRef}
        className="tab-key-blocker-container"
        style={{ width: '100%', height: '100%' }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        tabIndex={-1}
      >
        {this.props.children}
      </div>
    );
  }
}

export default TabKeyBlocker;

// Optional: Create a Higher Order Component (HOC)
export const withTabKeyBlocker = (WrappedComponent: React.ComponentType<any>, options?: Partial<TabKeyBlockerProps>) => {
  return (props: any) => (
    <TabKeyBlocker {...options}>
      <WrappedComponent {...props} />
    </TabKeyBlocker>
  );
};